import React, { useState } from "react";

import SEO from "../../components/SEO";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

import taxRules from "../../data/taxRules.json";

import { calculateTax }
from "../../utils/tax/calculateTax";

import TaxGraph
from "../../components/tax/TaxGraph";

import Comparison
from "../../components/tax/Comparison";

import { downloadPDF }
from "../../utils/tax/pdf";

import "../../styles/TaxCalculator.css";

export default function TaxCalculator() {

  const [salary, setSalary] =
    useState("");

  const [oldRes, setOldRes] =
    useState(null);

  const [newRes, setNewRes] =
    useState(null);

  const [graph, setGraph] =
    useState([]);

  const handleCalc = () => {

    const income =
      Number(salary);

    if (!income || income <= 0) {
      return;
    }

    const oldData =
      calculateTax(
        income,
        "old",
        taxRules
      );

    const newData =
      calculateTax(
        income,
        "new",
        taxRules
      );

    setOldRes(oldData);

    setNewRes(newData);

    let g = [];

    for (
      let i = 100000;
      i <= income;
      i += 100000
    ) {

      g.push({

        salary: i,

        tax: calculateTax(
          i,
          "old",
          taxRules
        ).tax,

      });

    }

    setGraph(g);
  };

  return (
    <>
      <SEO
        title="India Income Tax Calculator 2025 - Old vs New Regime"
        description="Calculate income tax online in India using old and new tax regimes. Compare tax slabs, deductions, cess, and download PDF tax reports instantly."
        keywords="india tax calculator, income tax calculator india, old vs new tax regime, tax slab calculator, salary tax calculator"
        url="https://online-tools-center.vercel.app/tax-calculator"
      />

      <Navbar />

      <div className="container py-4">

        <div className="row justify-content-center">

          <div className="col-lg-10">

            <div className="card shadow-lg border-0 rounded-4 p-4">

              <div className="text-center mb-4">

                <h1 className="fw-bold mb-3">
                  India Income Tax Calculator 2025
                </h1>

                <p className="text-muted">
                  Calculate income tax instantly
                  under old and new tax regimes
                  with detailed comparison charts
                  and downloadable PDF reports.
                </p>

                <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">

                  <span className="badge bg-light text-dark p-2">
                    ✓ Old vs New Regime
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Instant Results
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ PDF Download
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Free Tax Tool
                  </span>

                </div>

              </div>

              <div className="row g-4 align-items-center">

                <div className="col-md-6">

                  <label className="fw-semibold mb-2">
                    Annual Salary
                  </label>

                  <input
                    type="number"
                    placeholder="Enter Annual Salary"
                    className="form-control mb-3"
                    value={salary}
                    onChange={(e) =>
                      setSalary(e.target.value)
                    }
                  />

                  <button
                    className="btn btn-primary w-100 py-2 fw-semibold"
                    onClick={handleCalc}
                  >
                    Calculate Tax
                  </button>

                </div>

                <div className="col-md-6">

                  <div className="bg-light rounded-4 p-4">

                    <h4 className="fw-bold mb-3">
                      Why Use This Tax Calculator?
                    </h4>

                    <ul className="mb-0">

                      <li>
                        Compare old vs new tax regime
                      </li>

                      <li>
                        Instant salary tax estimation
                      </li>

                      <li>
                        Better financial planning
                      </li>

                      <li>
                        Download PDF tax reports
                      </li>

                      <li>
                        Accurate cess calculation
                      </li>

                    </ul>

                  </div>

                </div>

              </div>

              {oldRes && newRes && (
                <>

                  <div className="mt-5">

                    <Comparison
                      oldData={oldRes}
                      newData={newRes}
                    />

                  </div>

                  <div className="mt-5">

                    <div className="card border-0 shadow-sm rounded-4 p-3">

                      <h3 className="fw-bold mb-3">
                        Tax Analysis Chart
                      </h3>

                      <TaxGraph
                        data={graph}
                      />

                    </div>

                  </div>

                  <div className="mt-4">

                    <button
                      className="btn btn-dark px-4 py-2"
                      onClick={() =>
                        downloadPDF({
                          salary,
                          tax: oldRes.tax,
                          net: oldRes.net,
                        })
                      }
                    >
                      Download Salary PDF
                    </button>

                  </div>

                </>
              )}

            </div>

            {/* SEO CONTENT */}

<div className="mt-5">

  <h2 className="fw-bold mb-3">What is an Income Tax Calculator?</h2>
  <p>
    An income tax calculator is an online tool that estimates the amount of tax
    a salaried individual needs to pay to the government based on their annual
    income. In India, income tax is levied by the central government under the
    Income Tax Act, 1961, and is calculated based on applicable tax slabs,
    exemptions, deductions, and a 4% health and education cess applied on the
    total tax amount. Our calculator supports both the old and new tax regimes
    introduced by the Union Budget, allowing you to instantly compare your tax
    liability under each and choose the one that saves you more money.
  </p>

  <h2 className="fw-bold mt-5 mb-3">How Income Tax is Calculated in India</h2>
  <p>
    Income tax in India is calculated on your gross total income after
    subtracting applicable deductions and exemptions. The remaining amount,
    called taxable income, is then applied against the tax slab rates for the
    chosen regime. Under both regimes, a standard deduction of ₹50,000 is
    available for salaried employees. After computing the basic tax, a surcharge
    may apply for incomes above ₹50 lakh, and finally a 4% Health and Education
    Cess is added to arrive at the total tax payable. Our calculator handles all
    of these steps automatically — you simply enter your annual salary and get
    an instant, accurate result.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Old Tax Regime vs New Tax Regime — Which is Better?</h2>
  <p>
    The old tax regime allows taxpayers to claim a wide range of deductions and
    exemptions that reduce taxable income significantly. These include Section
    80C deductions of up to ₹1.5 lakh for investments in PPF, ELSS, LIC, and
    home loan principal repayment; Section 80D for health insurance premiums;
    HRA exemption for rent paid; Leave Travel Allowance (LTA); home loan
    interest deduction under Section 24(b); and several other allowances. If you
    have significant investments and claim multiple deductions, the old regime
    often results in lower total tax.
  </p>
  <p>
    The new tax regime, introduced in Budget 2020 and made the default from
    FY 2023-24 onwards, offers lower and simplified tax slab rates but
    eliminates most deductions and exemptions. It is particularly beneficial
    for younger taxpayers who are just starting their careers and do not yet
    have large investments or home loans. The new regime also has a higher
    basic exemption limit of ₹3 lakh compared to ₹2.5 lakh in the old regime,
    and incomes up to ₹7 lakh are effectively tax-free under the new regime
    due to the rebate under Section 87A. Use our calculator to enter your
    salary and instantly see which regime saves you more tax.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Income Tax Slabs for FY 2024-25</h2>
  <p>
    Under the new tax regime for FY 2024-25, income up to ₹3 lakh is exempt,
    income between ₹3 lakh and ₹7 lakh is taxed at 5%, between ₹7 lakh and
    ₹10 lakh at 10%, between ₹10 lakh and ₹12 lakh at 15%, between ₹12 lakh
    and ₹15 lakh at 20%, and income above ₹15 lakh at 30%. Under the old tax
    regime, income up to ₹2.5 lakh is exempt, between ₹2.5 lakh and ₹5 lakh
    is taxed at 5%, between ₹5 lakh and ₹10 lakh at 20%, and above ₹10 lakh
    at 30%. In both regimes, a 4% Health and Education Cess is added on the
    computed tax amount.
  </p>

  <h2 className="fw-bold mt-5 mb-3">How to Use This Tax Calculator</h2>
  <ol>
    <li className="mb-2">
      <strong>Enter your annual salary</strong> — type your total gross annual
      income in rupees in the input field.
    </li>
    <li className="mb-2">
      <strong>Click Calculate Tax</strong> — the tool instantly computes your
      tax liability under both the old and new tax regimes simultaneously.
    </li>
    <li className="mb-2">
      <strong>Review the comparison</strong> — see a side-by-side breakdown of
      tax payable, net salary, and effective tax rate under both regimes.
    </li>
    <li className="mb-2">
      <strong>Analyze the chart</strong> — the tax analysis graph shows how
      your tax grows across different income levels.
    </li>
    <li className="mb-2">
      <strong>Download your report</strong> — save a PDF of your tax calculation
      for your records or to share with your employer or CA.
    </li>
  </ol>

  <h2 className="fw-bold mt-5 mb-3">Frequently Asked Questions</h2>

  <div className="mb-4">
    <h5>Which tax regime is better for me?</h5>
    <p>
      It depends on your total deductions and investments. As a general rule,
      if your total deductions under the old regime (80C, HRA, 80D, home loan
      interest, etc.) exceed approximately ₹3.75 lakh for incomes above ₹15
      lakh, the old regime is likely better. For those with fewer deductions or
      lower incomes, the new regime often results in lower tax. Use this
      calculator to compare both and make the decision based on your specific
      numbers.
    </p>
  </div>

  <div className="mb-4">
    <h5>Can I switch between old and new tax regime every year?</h5>
    <p>
      Salaried employees without business income can switch between the old and
      new tax regimes every financial year. You simply inform your employer of
      your preferred regime at the start of each financial year so they can
      deduct TDS accordingly. If you have business or professional income,
      switching back from the new to the old regime is allowed only once in a
      lifetime.
    </p>
  </div>

  <div className="mb-4">
    <h5>Does this calculator include cess and surcharge?</h5>
    <p>
      Yes, the 4% Health and Education Cess is included in all calculations.
      For incomes above ₹50 lakh, a surcharge also applies — 10% surcharge for
      income between ₹50 lakh and ₹1 crore, and 15% for income above ₹1 crore
      under the old regime. Our calculator factors these in to give you an
      accurate total tax figure.
    </p>
  </div>

  <div className="mb-4">
    <h5>Is this tax calculator free?</h5>
    <p>
      Yes, this online income tax calculator is completely free to use with no
      registration required. It works on all devices including mobile phones,
      tablets, and desktop computers, and you can download a PDF report of your
      results at no cost.
    </p>
  </div>

  <div className="mt-5 p-4 bg-light rounded-4">
    <h3 className="fw-bold mb-3">Related Financial Tools</h3>
    <div className="d-flex flex-wrap gap-3">
      <a href="/emi-calculator" className="btn btn-outline-primary">EMI Calculator</a>
      <a href="/loan-eligibility" className="btn btn-outline-primary">Loan Eligibility</a>
      <a href="/percentage-calculator" className="btn btn-outline-primary">Percentage Calculator</a>
    </div>
  </div>

</div>

          </div>

        </div>

      </div>

      <Footer />

    </>
  );
}